import {
  callFromIncident,
  findCall,
  answerCall,
  nextCallDelay,
  defaultCallSettings,
} from "./emergencyCalls";
import {
  initializePatients,
  advancePatients,
  transportArrival,
  startTransport,
  allDelivered,
} from "./patients";
import { incidentProfiles, escalationPaths } from "./incidentProfiles";
import type { z } from "zod";
import { chooseProfile, profileFor, type ProfileId } from "./incidentProfiles";
import { available, reconcileAvailability } from "./availability";
import {
  recordRadioTransitions,
  requestStatus,
  requestSpeech,
  grantSpeech,
  acknowledgeSpeech,
  emitRadio,
  callsign,
  controlCallsign,
} from "./radio";
import {
  CommandSchema,
  type SiteSchema,
  fulfills,
  measureKinds,
  requirements,
  isOpen,
  type Command,
  type Incident,
  type SimState,
  type Vehicle,
} from "./model";
import { makeIncident } from "./scenarios";
import {
  canStationHost,
  stationFleet,
  specialtyPackages,
  vehicleDefinitions,
} from "./organizations";
import { defaultArea, isGermanyPoint } from "./regions";
import { pointInArea } from "./geography";
import {
  distanceKm,
  pendingRouting,
  createJourney,
  journeyPosition,
  type RoutingProvider,
} from "../providers/routing";
function add(
  s: SimState,
  i: Incident,
  text: string,
  type: Incident["timeline"][number]["type"],
) {
  i.timeline.push({
    id: `${s.revision}-${i.timeline.length}-${s.now}`,
    at: s.now,
    text,
    type,
  });
}
function incident(s: SimState, id: string) {
  const i = s.incidents.find((i) => i.id === id);
  if (!i) throw Error("Einsatz nicht gefunden.");
  return i;
}
function openIncident(s: SimState, id: string) {
  const i = incident(s, id);
  if (!isOpen(i)) throw Error("Dieser Einsatz ist bereits abgeschlossen.");
  return i;
}
function vehicle(s: SimState, id: string) {
  const v = s.vehicles.find((v) => v.id === id);
  if (!v) throw Error("Fahrzeug nicht gefunden.");
  return v;
}
export function coverage(s: SimState, i: Incident, arrived = false) {
  const req = requirements(i);
  return Object.entries(req).map(([kind, needed]) => ({
    kind: kind as Vehicle["kind"],
    needed: needed!,
    count: s.vehicles.filter(
      (v) =>
        v.incidentId === i.id &&
        [3, 4].includes(v.status) &&
        fulfills(v.kind, kind as Vehicle["kind"], i) &&
        (!arrived || v.status === 4),
    ).length,
  }));
}
function random(s: SimState) {
  s.seed = (s.seed * 1664525 + 1013904223) >>> 0;
  return s.seed / 4294967296;
}
export function generate(
  s: SimState,
  type?: Incident["type"],
  locationId?: number,
  report?: string,
  site?: z.infer<typeof SiteSchema>,
  profileId?: ProfileId,
) {
  if (s.realAddresses) {
    if (!site) {
      const pool = (s.addressPool ?? []).filter(
        (a) =>
          pointInArea(a.location, s.area, distanceKm) &&
          !s.incidents.some(
            (i) => isOpen(i) && i.addressSource?.id === a.addressSource.id,
          ) &&
          !(s.calls ?? []).some(
            (c) =>
              c.phase !== "ended" && c.addressSource?.id === a.addressSource.id,
          ),
      );
      if (!pool.length)
        throw Error(
          "Echte Einsatzadressen werden noch geladen. Bitte kurz warten.",
        );
      site = pool[Math.floor(random(s) * pool.length)];
    }
    if (!site.addressSource)
      throw Error("Bitte eine Adresse aus dem Kartenbestand auswählen.");
    s.addressPool = (s.addressPool ?? []).filter(
      (a) => a.addressSource.id !== site!.addressSource!.id,
    );
  }
  if (site && !isGermanyPoint(site.location))
    throw Error("Der Einsatzort muss im Deutschland-Kartenbereich liegen.");
  if (
    site &&
    s.area.controlCenterId &&
    !pointInArea(site.location, s.area, distanceKm)
  )
    throw Error(`Der Einsatzort liegt außerhalb von ${s.area.name}.`);
  if (s.incidents.filter(isOpen).length >= 12)
    throw Error(
      "Maximal 12 offene Einsätze. Bitte zuerst Einsätze abschließen.",
    );
  if (s.incidents.length >= 500)
    throw Error(
      "Archivgrenze erreicht. Spielstand exportieren und neue Sitzung beginnen.",
    );
  if (
    !site &&
    locationId === undefined &&
    (s.area.name !== defaultArea.name ||
      distanceKm(s.area.center, defaultArea.center) > 1)
  ) {
    const nearby = s.stations.filter((w) =>
      pointInArea(w.location, s.area, distanceKm),
    );
    const anchor = nearby[Math.floor(random(s) * nearby.length)];
    let point: [number, number] = [...(anchor?.location ?? s.area.center)];
    if (anchor) {
      for (let attempt = 0; attempt < 12; attempt++) {
        const candidate: [number, number] = [
          anchor.location[0] + (random(s) - 0.5) * 0.008,
          anchor.location[1] + (random(s) - 0.5) * 0.012,
        ];
        if (pointInArea(candidate, s.area, distanceKm)) {
          point = candidate;
          break;
        }
      }
    } else if (s.area.boundary?.length && s.area.bounds) {
      const [south, west, north, east] = s.area.bounds;
      for (let attempt = 0; attempt < 80; attempt++) {
        const candidate: [number, number] = [
          south + random(s) * (north - south),
          west + random(s) * (east - west),
        ];
        if (pointInArea(candidate, s.area, distanceKm)) {
          point = candidate;
          break;
        }
      }
    }
    site = {
      address: `${anchor?.name ?? s.area.name} · Umfeld (Übungsort)`,
      district: s.area.name,
      location: point,
    };
  }
  if (
    site &&
    locationId === undefined &&
    site.address.endsWith("· Umfeld (Übungsort)") &&
    !pointInArea(site.location, s.area, distanceKm)
  )
    site.location = s.area.boundary?.[0]?.[0]
      ? [...s.area.boundary[0][0]]
      : [...s.area.center];
  const roll = random(s);
  const incidentType =
    type ??
    (roll < 0.5
      ? "medizinisch"
      : roll < 0.68
        ? "brand"
        : roll < 0.8
          ? "vu"
          : roll < 0.94
            ? "polizei"
            : roll < 0.975
              ? "unwetter"
              : roll < 0.99
                ? "wasser"
                : "berg");
  const i = makeIncident(
    s,
    incidentType,
    locationId ?? Math.floor(random(s) * 8),
    report,
    site,
    profileId ?? (type ? undefined : chooseProfile(incidentType, random(s))),
  );
  // New cases receive patients; legacy active incidents keep their existing completion rules.
  initializePatients(i, i.profileId!);
  i.nextEscalationAt = s.now + (180 + random(s) * 360) * 1000;
  i.escalationRoll = random(s);
  s.incidents.unshift(i);
  return i;
}
function generateCall(s: SimState, profileId?: ProfileId) {
  const settings = s.callSettings ?? defaultCallSettings;
  if (
    (s.calls ?? []).filter((c) => c.phase === "ringing").length >=
    settings.maxWaiting
  )
    throw Error("Notrufwarteschlange ist voll.");
  const i = generate(
    s,
    profileId ? incidentProfiles[profileId].type : undefined,
    undefined,
    undefined,
    undefined,
    profileId,
  );
  s.incidents = s.incidents.filter((x) => x.id !== i.id);
  const c = callFromIncident(s, i);
  s.calls = [
    ...(s.calls ?? []).filter((c) => c.phase !== "ended").slice(-20),
    ...(s.calls ?? []).filter((c) => c.phase === "ended").slice(-70),
    c,
  ];
  emitRadio(s, {
    kind: "call",
    transmissions: [
      {
        sender: "Telefonie",
        text: "Eingehender Notruf. Bitte Gespräch annehmen.",
      },
    ],
  });
  s.nextCallAt = s.now + nextCallDelay(s);
  return c;
}
function updateIncidentCrews(s: SimState, i: Incident) {
  if (i.phase === "stabilisiert" || i.phase === "abgeschlossen") return;
  const units = s.vehicles.filter((v) => v.incidentId === i.id);
  const lead = units.find((v) => v.status === 4);
  i.phase = lead ? "vorort" : units.length ? "alarmiert" : "gemeldet";
  if (!lead) {
    delete i.firstArrivalAt;
    delete i.reconnaissanceAt;
    delete i.nextRadioAt;
  }
  const missing = coverage(s, i).filter((item) => item.count < item.needed);
  if (missing.length) {
    add(
      s,
      i,
      "Nach Abzug fehlen: " +
        missing
          .map((item) => item.needed - item.count + " " + item.kind)
          .join(", ") +
        ". Maßnahmen können nur mit ausreichenden Kräften fortgesetzt werden.",
      "warning",
    );
    if (lead) requestSpeech(s, lead, "reinforcement", true);
  }
}
function detach(
  s: SimState,
  v: Vehicle,
  mode: "standby" | "return",
  routing: RoutingProvider,
) {
  if (v.mutualAid) mode = "return";
  if (!v.incidentId || ![3, 4].includes(v.status))
    throw Error("Fahrzeug ist keinem aktiven Einsatz zugeordnet.");
  const i = incident(s, v.incidentId);
  if (v.journey?.routeStatus === "ready")
    v.location = [...journeyPosition(v.journey, s.now).position];
  const station = s.stations.find((w) => w.id === v.stationId)!;
  s.speechRequests = s.speechRequests.filter((r) => r.vehicleId !== v.id);
  add(
    s,
    i,
    v.name +
      " abgezogen · " +
      (mode === "return"
        ? "Rückfahrt zur Wache."
        : "Status 1, wartet am aktuellen Standort auf neuen Auftrag."),
    "status",
  );
  emitRadio(s, {
    kind: "conversation",
    vehicleId: v.id,
    incidentId: i.id,
    transmissions: [
      {
        sender: controlCallsign(s),
        text:
          callsign(v) +
          ", hier " +
          controlCallsign(s) +
          ". Einsatzauftrag aufgehoben. " +
          (mode === "return"
            ? "Rücken Sie zur Wache ein."
            : "Standort beibehalten und einsatzbereit melden.") +
          " Kommen.",
      },
      {
        sender: callsign(v),
        text: "Hier " + callsign(v) + ". Verstanden, Status 1. Ende.",
      },
    ],
  });
  v.incidentId = null;
  v.status = 1;
  v.journey =
    mode === "return"
      ? createJourney(v, station.location, s.now, true, station, routing)
      : null;
  updateIncidentCrews(s, i);
}
export function applyCommand(
  state: SimState,
  input: Command,
  routing: RoutingProvider = pendingRouting,
): SimState {
  const c = CommandSchema.parse(input);
  if (state.verifiedFleetOnly && ["saveStation", "saveVehicle", "equipStation"].includes(c.type))
    throw Error("Es sind ausschließlich belegte Katalogbestände zugelassen. Zusätzliche Kräfte bitte als Überlandhilfe anfordern.");
  const s = structuredClone(state);
  switch (c.type) {
    case "generateCall":
      generateCall(s, c.profileId);
      break;
    case "acceptCall": {
      const call = findCall(s, c.callId);
      if (call.phase !== "ringing")
        throw Error("Dieser Notruf wartet nicht mehr.");
      if (s.calls?.some((c) => c.phase === "active"))
        throw Error("Zuerst das laufende Telefongespräch beenden.");
      call.phase = "active";
      call.acceptedAt = s.now;
      call.answered.push("opening");
      break;
    }
    case "askCall":
      answerCall(s, c.callId, c.question);
      break;
    case "endCall": {
      const call = findCall(s, c.callId);
      if (call.phase !== "active") throw Error("Kein aktives Gespräch.");
      if (!call.incidentId)
        throw Error("Zuerst einen Einsatz aus dem Notruf erstellen.");
      call.phase = "ended";
      call.endedAt = s.now;
      break;
    }
    case "incidentFromCall": {
      const call = findCall(s, c.callId);
      if (call.phase !== "active" || call.incidentId)
        throw Error(
          "Notruf ist nicht aktiv oder bereits mit einem Einsatz verknüpft.",
        );
      const i = generate(
        s,
        incidentProfiles[c.profileId].type,
        undefined,
        c.report,
        {
          address: call.address,
          district: call.district,
          location: call.location,
          addressSource: call.addressSource,
          street: call.street,
          houseNumber: call.houseNumber,
          postcode: call.postcode,
        },
        c.profileId,
      );
      i.callerName = call.callerName;
      i.callId = call.id;
      i.actualProfileId = call.profileId;
      call.incidentId = i.id;
      i.patients = [];
      initializePatients(i, call.profileId);
      add(
        s,
        i,
        "Notruf übernommen. Standort automatisch verknüpft. Abgefragt: " +
          call.answered.join(", ") +
          ".",
        "call",
      );
      break;
    }
    case "transportPatient": {
      const i = openIncident(s, c.incidentId),
        p = i.patients?.find((p) => p.id === c.patientId),
        v = vehicle(s, c.vehicleId),
        h = s.hospitals?.find((h) => h.id === c.hospitalId);
      if (!p || !h) throw Error("Patient oder Krankenhaus nicht gefunden.");
      startTransport(s, i, p, v, h, routing);
      break;
    }
    case "setArea":
      if (!isGermanyPoint(c.area.center))
        throw Error(
          "Das Einsatzgebiet muss innerhalb des Deutschland-Kartenbereichs liegen.",
        );
      if (
        c.area.boundary?.some((ring) =>
          ring.some((point) => !isGermanyPoint(point)),
        )
      )
        throw Error("Der Leitstellenbereich enthält ungültige Koordinaten.");
      s.area = structuredClone(c.area);
      s.addressPool = (s.addressPool ?? []).filter((a) =>
        pointInArea(a.location, c.area, distanceKm),
      );
      break;
    case "equipStation": {
      const w = s.stations.find((w) => w.id === c.stationId);
      if (!w) throw Error("Wache nicht gefunden.");
      if (w.temporaryAid)
        throw Error("Fremdwachen können nicht verändert werden.");
      const kinds =
        c.packageId === "standard"
          ? stationFleet[w.service]
          : specialtyPackages[c.packageId].kinds;
      if (kinds.some((kind) => !canStationHost(w.service, kind)))
        throw Error("Das Ressourcenpaket passt nicht zu dieser Organisation.");
      const missing = kinds.filter(
        (kind) =>
          !s.vehicles.some((v) => v.stationId === w.id && v.kind === kind),
      );
      if (s.vehicles.length + missing.length > 4000)
        throw Error("Maximal 4.000 aktive Fahrzeuge.");
      for (const [index, kind] of missing.entries()) {
        const id = `v-${w.id}-${kind}-${s.revision}-${index}`;
        const suffix = ` ${kind} ${s.revision}-${index + 1}`;
        s.vehicles.push({
          id,
          name: `SIM ${w.name.slice(0, Math.max(8, 56 - suffix.length))}${suffix}`,
          kind,
          stationId: w.id,
          simulated: true,
          catalogManaged: false,
          status: 2,
          incidentId: null,
          location: [...w.location],
          journey: null,
        });
      }
      break;
    }
    case "dispatch": {
      const i = openIncident(s, c.incidentId);
      if (i.phase === "stabilisiert")
        throw Error(
          "Lage bereits stabilisiert. Einsatz kann abgeschlossen werden.",
        );
      const ids = [...new Set(c.vehicleIds)];
      const units = ids.map((id) => vehicle(s, id));
      if (units.some((v) => !available(v, s.now)))
        throw Error(
          "Mindestens ein Fahrzeug ist nicht verfügbar. Auswahl aktualisieren.",
        );
      if (units.some(v => v.mutualAid && v.mutualAid.requestedIncidentId !== i.id))
        throw Error("Überlandhilfe gilt ausschließlich für den angeforderten Einsatz.");
      for (const v of units) {
        v.journey = createJourney(
          v,
          i.location,
          s.now,
          false,
          s.stations.find((w) => w.id === v.stationId),
          routing,
        );
        v.status = 3;
        v.incidentId = i.id;
        add(
          s,
          i,
          `${v.name} alarmiert · ${v.journey.routeStatus === "pending" ? "Straßenroute wird berechnet" : `ETA ca. ${Math.ceil((v.journey.end - s.now) / 60000)} Min.`}.`,
          "dispatch",
        );
      }
      if (i.phase === "gemeldet") i.phase = "alarmiert";
      break;
    }
    case "grantSpeech":
      grantSpeech(s, c.requestId);
      break;
    case "acknowledgeSpeech":
      acknowledgeSpeech(s, c.requestId);
      break;
    case "requestSituation": {
      const v = vehicle(s, c.vehicleId);
      if (v.status !== 4 || !v.incidentId)
        throw Error(
          "Lageabfrage nur bei Kräften an der Einsatzstelle möglich.",
        );
      const i = openIncident(s, v.incidentId);
      if (s.speechRequests.some((r) => r.incidentId === i.id))
        throw Error("Für diesen Einsatz liegt bereits ein Sprechwunsch vor.");
      requestSpeech(s, v, i.phase === "stabilisiert" ? "completion" : "update");
      break;
    }
    case "requestStatus":
      requestStatus(s, c.vehicleId);
      break;
    case "createIncident":
      generate(s, c.incidentType, c.locationId, c.report, c.site, c.profileId);
      break;
    case "note":
      add(s, incident(s, c.incidentId), c.text, "note");
      break;
    case "measure": {
      const i = openIncident(s, c.incidentId);
      if (i.phase === "stabilisiert") throw Error("Lage bereits stabilisiert.");
      if (i.actions.includes(c.action))
        throw Error("Maßnahme wurde bereits durchgeführt.");
      if (c.action === "Brandbekämpfung" && i.type !== "brand")
        throw Error("Für diese Lage ist keine Brandbekämpfung vorgesehen.");
      const kinds = measureKinds[c.action];
      if (c.action === "Spezialkräfte einsetzen" && !profileFor(i).needs.SEK)
        throw Error(
          "Spezialkräfteeinsatz nur bei einer besonderen Polizeilage vorgesehen.",
        );
      if (
        !s.vehicles.some(
          (v) =>
            v.incidentId === i.id && kinds.includes(v.kind) && v.status === 4,
        )
      )
        throw Error(
          `Passende Kräfte (${kinds.join(", ")}) müssen an der Einsatzstelle sein.`,
        );
      i.actions.push(c.action);
      add(s, i, `${c.action} durch die Kräfte vor Ort eingeleitet.`, "measure");
      break;
    }
    case "release":
      detach(s, vehicle(s, c.vehicleId), "return", routing);
      break;
    case "withdraw": {
      const v = vehicle(s, c.vehicleId);
      if (v.incidentId !== c.incidentId)
        throw Error(
          "Fahrzeugauftrag wurde inzwischen geändert. Auswahl aktualisieren.",
        );
      detach(s, v, c.mode, routing);
      break;
    }
    case "returnToStation": {
      const v = vehicle(s, c.vehicleId);
      if (v.status !== 1 || v.incidentId || v.journey)
        throw Error("Zur Wache nur für freie, wartende Kräfte im Status 1.");
      const station = s.stations.find((w) => w.id === v.stationId)!;
      v.journey = createJourney(
        v,
        station.location,
        s.now,
        true,
        station,
        routing,
      );
      emitRadio(s, {
        kind: "conversation",
        vehicleId: v.id,
        transmissions: [
          {
            sender: controlCallsign(s),
            text:
              callsign(v) +
              ", hier " +
              controlCallsign(s) +
              ". Fahren Sie Ihre Heimatwache an. Ende.",
          },
        ],
      });
      break;
    }
    case "redirect": {
      const v = vehicle(s, c.vehicleId);
      if (v.mutualAid) throw Error("Fremdkräfte sind an ihren angeforderten Einsatz gebunden. Nach Abzug kehren sie zur Heimatwache zurück.");
      if (
        v.status !== 3 ||
        v.incidentId !== c.fromIncidentId ||
        c.fromIncidentId === c.incidentId
      )
        throw Error(
          "Umleiten nur für ein Fahrzeug auf Anfahrt zu einem anderen Einsatz.",
        );
      const target = openIncident(s, c.incidentId);
      if (target.phase === "stabilisiert")
        throw Error("Zieleinsatz ist bereits stabilisiert.");
      const remainingTurnout = Math.max(
        0,
        ((v.journey?.start ?? s.now) +
          (v.journey?.turnoutSeconds ?? 0) * 1000 -
          s.now) /
          1000,
      );
      detach(s, v, "standby", routing);
      v.journey = createJourney(
        v,
        target.location,
        s.now,
        false,
        s.stations.find((w) => w.id === v.stationId),
        routing,
      );
      v.journey.turnoutSeconds = remainingTurnout;
      if (v.journey.departureAt !== undefined && remainingTurnout > 0) {
        v.journey.departureAt += remainingTurnout * 1000;
        v.journey.end += remainingTurnout * 1000;
      }
      v.status = 3;
      v.incidentId = target.id;
      if (target.phase === "gemeldet") target.phase = "alarmiert";
      add(
        s,
        target,
        v.name +
          " vom bisherigen Auftrag umgeleitet. Neue Straßenroute ab aktueller Position wird berechnet.",
        "dispatch",
      );
      break;
    }
    case "close": {
      const i = openIncident(s, c.incidentId);
      if (!allDelivered(i))
        throw Error(
          "Zuerst alle Patienten ins Krankenhaus transportieren und übergeben.",
        );
      if (i.phase !== "stabilisiert")
        throw Error(
          "Der Einsatz kann erst nach Stabilisierung abgeschlossen werden.",
        );
      if (
        s.speechRequests.some(
          (r) => r.incidentId === i.id && r.reason === "completion",
        )
      )
        throw Error(
          "Zuerst Abschlussmeldung im Funk mit „Kommen“ und „Verstanden“ bearbeiten.",
        );
      s.speechRequests = s.speechRequests.filter((r) => r.incidentId !== i.id);
      i.phase = "abgeschlossen";
      add(
        s,
        i,
        "Einsatz abgeschlossen. Gebundene Kräfte treten die Rückfahrt an.",
        "system",
      );
      for (const v of s.vehicles.filter((v) => v.incidentId === i.id)) {
        const station = s.stations.find((w) => w.id === v.stationId)!;
        v.status = 1;
        v.incidentId = null;
        v.journey = createJourney(
          v,
          station.location,
          s.now,
          true,
          station,
          routing,
        );
      }
      break;
    }
    case "settings":
      if (c.running !== undefined) s.running = c.running;
      if (c.speed !== undefined) s.speed = c.speed;
      if (c.autoGenerate !== undefined) {
        s.autoGenerate = c.autoGenerate;
        s.nextCallAt = s.now + nextCallDelay(s);
      }
      if (c.callSettings) {
        if (c.callSettings.maxSeconds < c.callSettings.minSeconds)
          throw Error(
            "Maximales Intervall muss mindestens so groß wie das minimale sein.",
          );
        s.callSettings = c.callSettings;
        s.nextCallAt = s.now + nextCallDelay(s);
      }
      break;
    case "vehicleStatus": {
      const v = vehicle(s, c.vehicleId);
      if (v.incidentId || v.journey)
        throw Error("Statuswechsel nur bei Fahrzeugen auf der Wache möglich.");
      if (v.mutualAid)
        throw Error(
          "Fremdkräfte werden durch ihre Heimatleitstelle verwaltet.",
        );
      v.status = c.status;
      v.scheduleOffDuty = false;
      break;
    }
    case "saveStation": {
      const existing = s.stations.find((w) => w.id === c.id);
      if (c.id && !existing) throw Error("Wache nicht gefunden.");
      if (existing?.temporaryAid)
        throw Error("Fremdwachen können nicht verändert werden.");
      if (
        existing &&
        s.vehicles.some(
          (v) => v.stationId === existing.id && (v.journey || v.incidentId),
        )
      )
        throw Error(
          "Wache hat ausgerückte Fahrzeuge. Änderung nach deren Rückkehr möglich.",
        );
      if (
        existing &&
        s.vehicles.some(
          (v) =>
            v.stationId === existing.id && !canStationHost(c.service, v.kind),
        )
      )
        throw Error("Organisation passt nicht zu den vorhandenen Fahrzeugen.");
      if (!existing && s.stations.length >= 1200)
        throw Error(
          "Maximal 1.200 aktive Wachen; der Deutschlandkatalog bleibt vollständig durchsuchbar.",
        );
      const w = {
        id: existing?.id ?? `w-${s.revision}-${s.now}`,
        name: c.name,
        service: c.service,
        location: c.location,
        organization: c.organization,
        stationType: c.stationType,
        source: existing?.source ?? { kind: "manual" as const },
        level: existing?.level ?? 1,
        catalogCustomized: true,
      };
      if (existing) {
        Object.assign(existing, w);
        for (const v of s.vehicles.filter((v) => v.stationId === w.id))
          v.location = [...w.location];
      } else s.stations.push(w);
      break;
    }
    case "saveVehicle": {
      const w = s.stations.find((w) => w.id === c.stationId);
      if (!w) throw Error("Wache nicht gefunden.");
      if (w.temporaryAid)
        throw Error("Fremdwachen können nicht verändert werden.");
      if (!canStationHost(w.service, c.kind))
        throw Error(
          "Fahrzeugtyp und Organisation der Wache passen nicht zusammen.",
        );
      const v = s.vehicles.find((v) => v.id === c.id);
      if (c.id && !v) throw Error("Fahrzeug nicht gefunden.");
      if (v?.mutualAid)
        throw Error("Fremdkräfte können nicht verändert werden.");
      if (v && (v.journey || v.incidentId))
        throw Error("Ausgerücktes Fahrzeug kann nicht bearbeitet werden.");
      if (
        s.vehicles.some((other) => other.name === c.name && other.id !== c.id)
      )
        throw Error("Funkrufname ist bereits vergeben.");
      if (v)
        Object.assign(v, {
          name: c.name,
          kind: c.kind,
          simulated: true,
          catalogManaged: false,
          catalogRetire: false,
          policeBaseline: undefined,
          evidence: undefined,
          stationId: w.id,
          location: [...w.location],
        });
      else {
        if (s.vehicles.length >= 4000)
          throw Error("Maximal 4.000 aktive Fahrzeuge.");
        s.vehicles.push({
          id: `v-${s.revision}-${s.now}`,
          name: c.name,
          kind: c.kind,
          simulated: true,
          catalogManaged: false,
          catalogRetire: false,
          evidence: undefined,
          stationId: w.id,
          location: [...w.location],
          status: 2,
          incidentId: null,
          journey: null,
        });
      }
      break;
    }
  }
  s.vehicles = s.vehicles.filter(
    (v) => !(v.catalogRetire && !v.incidentId && !v.journey),
  );
  reconcileAvailability(s);
  recordRadioTransitions(state, s);
  s.revision++;
  return s;
}
export function advance(state: SimState, realSeconds: number): SimState {
  if (!state.running || realSeconds <= 0) return state;
  const s = structuredClone(state);
  const seconds = Math.min(realSeconds, 5) * s.speed;
  s.now += seconds * 1000;
  for (const v of s.vehicles) {
    if ([7, 8].includes(v.status) !== Boolean(v.transport))
      throw Error("Ungültige Transportbindung.");
    if (v.transport) {
      const p = s.incidents
        .find((i) => i.id === v.incidentId)
        ?.patients?.find((p) => p.id === v.transport!.patientId);
      if (
        !p ||
        p.vehicleId !== v.id ||
        p.hospitalId !== v.transport.hospitalId ||
        !s.hospitals?.some((h) => h.id === p.hospitalId) ||
        p.phase !== (v.status === 7 ? "transport" : "handover") ||
        (v.status === 7 && !v.journey) ||
        (v.status === 8 && (v.journey || !v.transport.handoverUntil))
      )
        throw Error("Ungültiger Patiententransport.");
    }
    const j = v.journey;
    if (!j) continue;
    if (j.routeStatus !== "ready" || !j.geometry) continue;
    const progress = journeyPosition(j, s.now);
    v.location = [...progress.position];
    if (progress.arrived) {
      v.journey = null;
      if (v.status === 7) {
        transportArrival(s, v);
        continue;
      }
      v.status = j.returning ? 2 : 4;
      if (v.incidentId) {
        const i = incident(s, v.incidentId);
        if (i.phase === "gemeldet" || i.phase === "alarmiert")
          i.phase = "vorort";
        i.firstArrivalAt ??= s.now;
        add(s, i, `${v.name} an Einsatzstelle · Status 4.`, "status");
        if (v.kind === "HvO")
          add(
            s,
            i,
            "HvO eingetroffen: Erstversorgung und erste Rückmeldung möglich. Regulärer Rettungsdienst weiterhin erforderlich.",
            "status",
          );
      }
    }
  }
  for (const i of s.incidents.filter(
    (i) => isOpen(i) && i.phase !== "stabilisiert",
  )) {
    const onScene = s.vehicles.some(
      (v) => v.incidentId === i.id && v.status === 4,
    );
    let profile = profileFor(i);
    if (
      onScene &&
      i.firstArrivalAt !== undefined &&
      i.reconnaissanceAt === undefined &&
      s.now - i.firstArrivalAt >= 30000
    ) {
      i.reconnaissanceAt = s.now;
      if (i.actualProfileId && i.actualProfileId !== i.profileId) {
        i.profileId = i.actualProfileId;
        const actual = profileFor(i);
        i.type = actual.type;
        i.title = actual.title;
        add(
          s,
          i,
          "Erkundung präzisiert das Stichwort: " +
            actual.title +
            ". Kräftebedarf prüfen.",
          "warning",
        );
      }
      const missing = coverage(s, i).filter((r) => r.count < r.needed);
      add(
        s,
        i,
        "Erkundung: " +
          profile.title +
          ". " +
          (missing.length
            ? "Noch anzufordern: " +
              missing.map((r) => r.needed - r.count + " " + r.kind).join(", ") +
              ". Bereits alarmierte Kräfte berücksichtigt."
            : "Benötigte Kräfte sind vor Ort oder bereits auf Anfahrt."),
        missing.length ? "warning" : "status",
      );
    }
    profile = profileFor(i);
    if (
      i.nextEscalationAt !== undefined &&
      s.now >= i.nextEscalationAt &&
      i.sceneResolvedAt === undefined
    ) {
      const path = escalationPaths[i.actualProfileId ?? i.profileId!];
      if (path && (i.escalationRoll ?? 1) < path.chance && i.escalation < 2) {
        i.profileId = path.to;
        i.actualProfileId = path.to;
        profile = profileFor(i);
        i.type = profile.type;
        i.title = profile.title;
        initializePatients(i, path.to);
        i.escalation++;
        i.workSeconds = Math.min(i.workSeconds, 60);
        add(
          s,
          i,
          path.text + " Erhöhten Kräftebedarf prüfen und nachalarmieren.",
          "warning",
        );
      }
      i.nextEscalationAt = s.now + (240 + random(s) * 360) * 1000;
      i.escalationRoll = random(s);
    }
    // A missing ambulance must not make a fire spread when sufficient fire crews are already working.
    const fireCovered = coverage(s, i, true)
      .filter((r) => r.kind === "HLF" || r.kind === "LF")
      .every((r) => r.count >= r.needed);
    if (
      profile.spread &&
      !fireCovered &&
      i.escalation < 2 &&
      (s.now - i.createdAt) / 1000 >= [240, 540][i.escalation]
    ) {
      i.escalation++;
      const deficit = coverage(s, i)
        .filter((r) => r.count < r.needed)
        .map((r) => r.needed - r.count + " " + r.kind);
      add(
        s,
        i,
        (i.escalation === 1
          ? "Brandausbreitung gemeldet. "
          : "Brand hat weitere Räume erfasst. ") +
          (deficit.length
            ? "Zusätzlich anzufordern: " + deficit.join(", ") + "."
            : "Zusätzliche Kräfte bereits alarmiert."),
        "warning",
      );
    }
    const adequate = coverage(s, i, true).every((r) => r.count >= r.needed);
    if (adequate) i.workSeconds += seconds;
    if (
      adequate &&
      i.workSeconds >= profile.workSeconds + i.escalation * 90 &&
      i.sceneResolvedAt === undefined
    ) {
      i.sceneResolvedAt = s.now;
      if (allDelivered(i)) i.phase = "stabilisiert";
      add(
        s,
        i,
        !allDelivered(i)
          ? "Einsatzstelle gesichert. Versorgung und Krankentransporte laufen weiter."
          : i.type === "brand"
            ? "Feuer unter Kontrolle. Personen gerettet, Nachkontrolle abgeschlossen. Einsatz kann abgeschlossen werden."
            : i.type === "vu"
              ? i.profileId === "vu-leicht"
                ? "Verletzte Person versorgt. Einsatz kann abgeschlossen werden."
                : "Personen befreit und versorgt. Gefahrenstelle gesichert. Einsatz kann abgeschlossen werden."
              : "Lage durch erforderliche Kräfte stabilisiert. Einsatz kann abgeschlossen werden.",
        "measure",
      );
    }
  }
  advancePatients(s, seconds);
  for (const i of s.incidents) {
    if (
      isOpen(i) &&
      i.phase !== "stabilisiert" &&
      i.sceneResolvedAt !== undefined &&
      allDelivered(i)
    ) {
      i.phase = "stabilisiert";
      add(
        s,
        i,
        "Alle Patienten übergeben und Einsatzmaßnahmen abgeschlossen. Abschluss möglich.",
        "system",
      );
    }
  }
  if (s.autoGenerate && s.now >= s.nextCallAt) {
    const settings = s.callSettings ?? defaultCallSettings;
    if (
      (s.calls ?? []).filter((c) => c.phase === "ringing").length <
        settings.maxWaiting &&
      s.incidents.filter(isOpen).length < 12 &&
      s.incidents.length < 500
    ) {
      try {
        generateCall(s);
      } catch {
        s.nextCallAt = s.now + 30000;
      }
    } else s.nextCallAt = s.now + 30000;
  }
  s.vehicles = s.vehicles.filter(
    (v) => !(v.catalogRetire && !v.incidentId && !v.journey),
  );
  reconcileAvailability(s);
  recordRadioTransitions(state, s);
  s.revision++;
  return s;
}
export function validateRelations(s: SimState) {
  const unique = (ids: string[]) => new Set(ids).size === ids.length;
  if (
    !unique(s.vehicles.map((v) => v.id)) ||
    !unique(s.stations.map((w) => w.id)) ||
    !unique(s.incidents.map((i) => i.id))
  )
    throw Error("Doppelte IDs im Spielstand.");
  if (
    !unique(s.radioMessages.map((m) => m.id)) ||
    s.radioMessages.some(
      (m, index) =>
        m.sequence >= s.nextRadioSequence ||
        (index > 0 && m.sequence <= s.radioMessages[index - 1].sequence),
    )
  )
    throw Error("Ungültige Funkprotokoll-Reihenfolge.");
  if (s.incidents.some((i) => i.number >= s.nextNumber))
    throw Error("Ungültige Einsatznummern.");
  if (s.incidents.some((i) => profileFor(i).type !== i.type))
    throw Error("Einsatzprofil passt nicht zur Einsatzart.");
  if (
    !unique(s.speechRequests.map((r) => r.id)) ||
    !unique(s.speechRequests.map((r) => r.incidentId)) ||
    s.speechRequests.some(
      (r) =>
        !s.vehicles.some(
          (v) =>
            v.id === r.vehicleId &&
            v.incidentId === r.incidentId &&
            v.status === 4,
        ) ||
        !s.incidents.some((i) => i.id === r.incidentId && isOpen(i)) ||
        (r.phase === "reported" && (!r.report || r.reportedAt === undefined)),
    )
  )
    throw Error("Ungültiger Sprechwunsch im Spielstand.");
  if (
    (s.calls ?? []).filter((c) => c.phase === "active").length > 1 ||
    !unique((s.calls ?? []).map((c) => c.id))
  )
    throw Error("Ungültige Notrufwarteschlange.");
  for (const call of s.calls ?? [])
    if (
      call.incidentId &&
      !s.incidents.some((i) => i.id === call.incidentId && i.callId === call.id)
    )
      throw Error("Ungültige Notrufverknüpfung.");
  for (const i of s.incidents) {
    if (!unique((i.patients ?? []).map((p) => p.id)))
      throw Error("Doppelte Patienten.");
    if (i.phase === "abgeschlossen" && !allDelivered(i))
      throw Error("Abgeschlossener Einsatz enthält offene Patienten.");
    for (const p of i.patients ?? [])
      if (
        ["transport", "handover"].includes(p.phase) &&
        !s.vehicles.some(
          (v) =>
            v.id === p.vehicleId &&
            v.transport?.patientId === p.id &&
            v.incidentId === i.id,
        )
      )
        throw Error("Patient ohne Transportmittel.");
  }
  for (const v of s.vehicles) {
    const w = s.stations.find((w) => w.id === v.stationId);
    if (!w || !canStationHost(w.service, v.kind))
      throw Error("Ungültige Wachenreferenz.");
    if (
      v.incidentId &&
      !s.incidents.some((i) => i.id === v.incidentId && isOpen(i))
    )
      throw Error("Ungültige Einsatzreferenz.");
    if ([3, 4, 7, 8].includes(v.status) !== Boolean(v.incidentId))
      throw Error("Ungültige Fahrzeugbindung.");
    if (v.status === 3 && (!v.journey || v.journey.returning))
      throw Error("Ungültige Anfahrt.");
    if (
      v.journey &&
      (v.journey.end <= v.journey.start ||
        !(
          (v.status === 1 && v.journey.returning) ||
          ([3, 7].includes(v.status) && !v.journey.returning)
        ))
    )
      throw Error("Ungültige Fahrt.");
    if ([7, 8].includes(v.status) !== Boolean(v.transport))
      throw Error("Ungültige Transportbindung.");
    if (v.transport) {
      const p = s.incidents
        .find((i) => i.id === v.incidentId)
        ?.patients?.find((p) => p.id === v.transport!.patientId);
      if (
        !p ||
        p.vehicleId !== v.id ||
        p.hospitalId !== v.transport.hospitalId ||
        !s.hospitals?.some((h) => h.id === p.hospitalId) ||
        p.phase !== (v.status === 7 ? "transport" : "handover") ||
        (v.status === 7 && !v.journey) ||
        (v.status === 8 && (v.journey || !v.transport.handoverUntil))
      )
        throw Error("Ungültiger Patiententransport.");
    }
    const j = v.journey;
    if (
      j?.routeStatus === "ready" &&
      (!j.geometry ||
        !j.segmentSeconds ||
        j.segmentSeconds.length !== j.geometry.length - 1 ||
        j.departureAt === undefined ||
        j.departureAt < j.start ||
        j.end <= j.departureAt ||
        Math.abs(
          j.segmentSeconds.reduce((a, b) => a + b, 0) -
            (j.end - j.departureAt) / 1000,
        ) > 0.1)
    )
      throw Error("Ungültiger Routenverlauf oder Fahrzeit.");
  }
}
